"use client"

import { useState } from "react"
import "./FriendsSystem.css"

const FriendsSystem = ({ gameState, setGameState, addNotification }) => {
  const [friends, setFriends] = useState([
    {
      id: 1,
      username: "SpaceCommander",
      level: 15,
      planet: "Mars",
      population: 2500,
      lastOnline: "2 hours ago",
      defenseRating: 85,
      resources: {
        energy: 450,
        materials: 320,
        food: 180,
        water: 380,
        oxygen: 420,
        knowledge: 95,
      },
      canRaid: true,
    },
    {
      id: 2,
      username: "CosmicExplorer",
      level: 12,
      planet: "Europa",
      population: 1800,
      lastOnline: "1 day ago",
      defenseRating: 72,
      resources: {
        energy: 380,
        materials: 290,
        food: 150,
        water: 340,
        oxygen: 390,
        knowledge: 120,
      },
      canRaid: true,
    },
  ])

  const [activeTab, setActiveTab] = useState("friends")
  const [searchQuery, setSearchQuery] = useState("")
  const [raidHistory, setRaidHistory] = useState([
    {
      id: 1,
      target: "SpaceCommander",
      type: "outgoing",
      success: true,
      resources: { energy: 50, materials: 30 },
      timestamp: Date.now() - 3600000,
    },
  ])

  const raidFriend = (friend) => {
    if (!friend.canRaid) {
      addNotification({
        type: "warning",
        title: "Raid Cooldown",
        description: "Cannot raid this friend yet. Wait for cooldown.",
      })
      return
    }

    const attackPower = gameState.user.level * 10 + Math.random() * 50
    const defensePower = friend.defenseRating

    const successChance = Math.max(0.1, Math.min(0.9, attackPower / (attackPower + defensePower)))
    const isSuccess = Math.random() < successChance

    if (isSuccess) {
      const stealPercentage = 0.1 + Math.random() * 0.2
      const stolenResources = {}

      Object.keys(friend.resources).forEach((resource) => {
        const amount = Math.floor(friend.resources[resource] * stealPercentage)
        if (amount > 0) {
          stolenResources[resource] = amount
        }
      })

      const newResources = { ...gameState.resources }
      Object.keys(stolenResources).forEach((resource) => {
        newResources[resource] = (newResources[resource] || 0) + stolenResources[resource]
      })

      setGameState((prev) => ({ ...prev, resources: newResources }))

      addNotification({
        type: "success",
        title: "Raid Successful",
        description: `Stolen: ${Object.entries(stolenResources)
          .map(([k, v]) => `${v} ${k}`)
          .join(", ")}`,
      })

      // Add to raid history
      setRaidHistory((prev) => [
        {
          id: Date.now(),
          target: friend.username,
          type: "outgoing",
          success: true,
          resources: stolenResources,
          timestamp: Date.now(),
        },
        ...prev.slice(0, 9),
      ])

      // Set cooldown
      setFriends((prev) => prev.map((f) => (f.id === friend.id ? { ...f, canRaid: false } : f)))

      setTimeout(
        () => {
          setFriends((prev) => prev.map((f) => (f.id === friend.id ? { ...f, canRaid: true } : f)))
        },
        24 * 60 * 60 * 1000,
      ) // 24 hour cooldown
    } else {
      addNotification({
        type: "error",
        title: "Raid Failed",
        description: "Their defenses were too strong!",
      })

      setRaidHistory((prev) => [
        {
          id: Date.now(),
          target: friend.username,
          type: "outgoing",
          success: false,
          resources: {},
          timestamp: Date.now(),
        },
        ...prev.slice(0, 9),
      ])
    }
  }

  return (
    <div className="friends-system">
      <div className="friends-header">
        <h2 className="section-title">
          <span className="title-icon">👥</span>
          Colony Network
        </h2>
        <p className="section-subtitle">Connect with other commanders and engage in resource raids</p>
      </div>

      <div className="friends-tabs">
        <button
          className={`tab-button ${activeTab === "friends" ? "active" : ""}`}
          onClick={() => setActiveTab("friends")}
        >
          <span className="tab-icon">👥</span>
          Friends ({friends.length})
        </button>
        <button className={`tab-button ${activeTab === "raids" ? "active" : ""}`} onClick={() => setActiveTab("raids")}>
          <span className="tab-icon">⚔️</span>
          Raid History ({raidHistory.length})
        </button>
        <button
          className={`tab-button ${activeTab === "search" ? "active" : ""}`}
          onClick={() => setActiveTab("search")}
        >
          <span className="tab-icon">🔍</span>
          Find Friends
        </button>
      </div>

      <div className="friends-content">
        {activeTab === "friends" && (
          <div className="friends-list">
            <div className="friends-grid">
              {friends.map((friend) => (
                <div key={friend.id} className="friend-card">
                  <div className="friend-header">
                    <div className="friend-info">
                      <h3 className="friend-name">{friend.username}</h3>
                      <div className="friend-details">
                        <span className="level-badge">Level {friend.level}</span>
                        <span className="planet-badge">🔴 {friend.planet}</span>
                      </div>
                    </div>
                    <div className="online-status">
                      <span className={`status-dot ${friend.lastOnline === "Online" ? "online" : "offline"}`}></span>
                      <span className="last-seen">{friend.lastOnline}</span>
                    </div>
                  </div>

                  <div className="colony-stats">
                    <div className="stat">
                      <span className="stat-label">Population:</span>
                      <span className="stat-value">{friend.population.toLocaleString()}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Defense:</span>
                      <span className="stat-value">{friend.defenseRating}</span>
                    </div>
                  </div>

                  <div className="resources-preview">
                    <h4>Available Resources:</h4>
                    <div className="resource-grid">
                      {Object.entries(friend.resources).map(([resource, amount]) => (
                        <div key={resource} className="resource-item">
                          <span className="resource-icon">
                            {resource === "energy" && "⚡"}
                            {resource === "materials" && "🔧"}
                            {resource === "food" && "🍎"}
                            {resource === "water" && "💧"}
                            {resource === "oxygen" && "💨"}
                            {resource === "knowledge" && "🧠"}
                          </span>
                          <span className="resource-amount">{amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="friend-actions">
                    <button
                      className={`raid-button ${friend.canRaid ? "available" : "cooldown"}`}
                      onClick={() => raidFriend(friend)}
                      disabled={!friend.canRaid}
                    >
                      {friend.canRaid ? "⚔️ Raid Colony" : "🕐 Cooldown"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "raids" && (
          <div className="raid-history">
            <h3 className="panel-title">Recent Raids</h3>
            <div className="raid-list">
              {raidHistory.map((raid) => (
                <div key={raid.id} className={`raid-item ${raid.type} ${raid.success ? "success" : "failed"}`}>
                  <div className="raid-info">
                    <div className="raid-target">
                      <span className="raid-type">
                        {raid.type === "outgoing" ? "⚔️ Attacked" : "🛡️ Defended against"}
                      </span>
                      <span className="target-name">{raid.target}</span>
                    </div>
                    <div className="raid-time">{new Date(raid.timestamp).toLocaleString()}</div>
                  </div>

                  <div className="raid-result">
                    <span className={`result-status ${raid.success ? "success" : "failed"}`}>
                      {raid.success ? "SUCCESS" : "FAILED"}
                    </span>
                    {raid.success && Object.keys(raid.resources).length > 0 && (
                      <div className="stolen-resources">
                        Gained:{" "}
                        {Object.entries(raid.resources)
                          .map(([k, v]) => `${v} ${k}`)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "search" && (
          <div className="friend-search">
            <h3 className="panel-title">Find New Allies</h3>
            <div className="search-section">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search by username..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <button className="search-button">🔍 Search</button>
              </div>
              <div className="search-results">
                <p>Search for other commanders to add as friends and raid their colonies!</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FriendsSystem
